import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import type { HubCategory } from '@/types';
import { categoryScene, categorySceneVideo } from '@/lib/gameArt';

interface CategoryPanelProps {
  category: HubCategory;
  label: string;
  cta: string;
  soonLabel: string;
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
}

/**
 * One column of the category rail. Closed it reads as a spine — a vertical
 * name over dimmed key art; open it widens, colours up and offers the link.
 */
export function CategoryPanel({
  category,
  label,
  cta,
  soonLabel,
  open,
  onOpen,
  onClose,
}: CategoryPanelProps) {
  const disabled = Boolean(category.comingSoon);
  const video = categorySceneVideo(category.id);
  const poster = categoryScene(category.id);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;

    if (open) {
      void el.play().catch(() => {
        /* hover play can be blocked */
      });
      return;
    }

    el.pause();
    if (el.readyState >= 1) el.currentTime = 0.05;
  }, [open]);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;

    const pinFirstFrame = () => {
      if (!open && el.readyState >= 1) el.currentTime = 0.05;
    };

    el.addEventListener('loadeddata', pinFirstFrame);
    return () => el.removeEventListener('loadeddata', pinFirstFrame);
  }, [open]);

  const classes = [
    'cat-panel',
    open ? 'is-open' : '',
    disabled ? 'is-disabled' : '',
  ].filter(Boolean);

  return (
    <Link
      to={disabled ? '#' : `/library?category=${category.id}`}
      className={classes.join(' ')}
      aria-disabled={disabled || undefined}
      onClick={(event) => disabled && event.preventDefault()}
      onPointerEnter={(event) => event.pointerType !== 'touch' && onOpen()}
      onFocus={onOpen}
      onBlur={onClose}
    >
      {video ? (
        <video
          ref={videoRef}
          className="cat-panel__art"
          src={video}
          muted
          loop
          playsInline
          preload="auto"
          aria-hidden="true"
        />
      ) : (
        <img
          className="cat-panel__art"
          src={poster}
          alt=""
          aria-hidden="true"
          loading="lazy"
          decoding="async"
        />
      )}

      <span className="cat-panel__wash" aria-hidden="true" />
      <span className="cat-panel__edge" aria-hidden="true" />

      {/* Decorative duplicate of the open face, so it stays out of the a11y tree. */}
      <span className="cat-panel__spine" aria-hidden="true">
        <span className="cat-panel__spine-label display">{label}</span>
      </span>

      <span className="cat-panel__reveal">
        <span className="cat-panel__title display">{label}</span>
        {disabled ? (
          <span className="cat-panel__soon">{soonLabel}</span>
        ) : (
          <span className="cat-panel__cta">
            {cta}
            <svg viewBox="0 0 16 16" width="15" height="15" aria-hidden="true">
              <path
                d="M2.5 8h10M9 4.5 12.5 8 9 11.5"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        )}
      </span>
    </Link>
  );
}
